import { create } from 'zustand';

import {
  ICamSettings,
  IExitRoomSettings,
  IGeneralActionBasicSettings,
  IGeneralActionSettings,
  IMicSettings,
  IShareScreenSettings,
} from '@src/types';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge/types';
import { useRoomStore } from './room.store';

interface IRoomActionsStore {
  // Mic state
  micSettings: IMicSettings;
  setMicSettings: (settings: Partial<IMicSettings>) => void;

  // Cam state
  camSettings: ICamSettings;
  setCamSettings: (settings: Partial<ICamSettings>) => void;

  // Exit room
  exitRoomSettings: IExitRoomSettings;
  setExitRoomSettings: (settings: Partial<IExitRoomSettings>) => void;

  // Raise hand
  raiseHandSettings: IGeneralActionSettings;
  setRaiseHandSettings: (settings: Partial<IGeneralActionSettings>) => void;

  // Share screen
  shareScreenSettings: IShareScreenSettings;
  setShareScreenSettings: (settings: Partial<IShareScreenSettings>) => void;

  // Share screen
  transcriptionSettings: IGeneralActionSettings;
  setTranscriptionSettings: (settings: Partial<IGeneralActionSettings>) => void;

  // Share Room
  shareRoomSettings: IGeneralActionSettings;
  setShareRoomSettings: (settings: Partial<IGeneralActionSettings>) => void;

  // Screen recording
  screenRecordingSettings: IGeneralActionSettings;
  setScreenRecordingSettings: (settings: Partial<IGeneralActionSettings>) => void;

  // Snapshot settings
  snapshotSettings: IGeneralActionBasicSettings;
  setSnapshotSettings: (settings: Partial<IGeneralActionBasicSettings>) => void;

  // Fullscreen settings
  fullscreenSettings: IGeneralActionBasicSettings;
  setFullscreenSettings: (settings: Partial<IGeneralActionBasicSettings>) => void;

  // Hide self settings
  hideSelfSettings: IGeneralActionBasicSettings;
  setHideSelfSettings: (settings: Partial<IGeneralActionBasicSettings>) => void;

  whiteboardSettings: IGeneralActionSettings;
  setWhiteboardSettings: (settings: Partial<IGeneralActionSettings>) => void;

  pipSettings: IGeneralActionSettings;
  setPipSettings: (settings: Partial<IGeneralActionSettings>) => void;
}

export const useRoomActionsStore = create<IRoomActionsStore>((set) => ({
  pipSettings: {
    isActive: false,
    isDisabled: false,
    onClickCallback: null,
    onClick: (isPipActive: any) => {
      set((state) => {
        const { pipSettings } = state;
        return {
          ...state,
          pipSettings: { ...pipSettings, isActive: isPipActive },
        };
      });
    },
  },
  setPipSettings: (micSettings) =>
    set((state) => ({
      micSettings: { ...state.micSettings, ...micSettings }, // Safely merge settings
    })),
  micSettings: {
    isMicClosed: true,
    isMicButtonDisabled: false,
    onMicClickCallback: null, // Initialize with null or a valid callback later
    onMicClick: (isMuted) => {
      set((state) => {
        const { micSettings } = state;
        // Ensure that onMicClickCallback is not null before calling it
        if (micSettings.onMicClickCallback) {
          micSettings.onMicClickCallback(isMuted); // Call the callback if it exists
        } else {
          console.warn('onMicClickCallback does not exist');
        }
        return {
          ...state,
          micSettings: { ...micSettings, isMicClosed: isMuted },
        };
      });
    },
  },
  setMicSettings: (micSettings) =>
    set((state) => ({
      micSettings: { ...state.micSettings, ...micSettings }, // Safely merge settings
    })),
  camSettings: {
    isCamClosed: true,
    isCamButtonDisabled: false,
    onCamClickCallback: null, // Initialize with null or a valid callback later
    onCamClick: async (isClosed) => {
      // Use async/await if the callback is expected to return a promise
      const { camSettings } = useRoomActionsStore.getState();
      if (camSettings.onCamClickCallback) {
        try {
          await camSettings.onCamClickCallback(isClosed);
        } catch (error) {
          console.warn('onCamClickCallback threw an error:', error);
        }
      } else {
        console.warn('onCamClickCallback does not exist');
      }
    },
  },
  setCamSettings: (camSettings) =>
    set((state) => ({
      camSettings: { ...state.camSettings, ...camSettings }, // Safely merge settings
    })),
  exitRoomSettings: {
    onExitCallback: null,
    onExitClick: (closeRoom = false, isBanned = false) => {
      set((state) => {
        const { exitRoomSettings } = state;
        // Ensure hasExitEventsSent handled correctly
        sendEventToWebView({ eventType: EWebViewEvents.MEETING_ENDED });

        if (exitRoomSettings.onExitCallback) {
          exitRoomSettings.onExitCallback(closeRoom, isBanned);
        } else {
          console.warn('onExitCallback does not exist');
        }
        return state;
      });
    },
  },
  setExitRoomSettings: (exitRoomSettings) =>
    set((state) => ({
      exitRoomSettings: { ...state.exitRoomSettings, ...exitRoomSettings }, // Safely merge settings
    })),
  raiseHandSettings: {
    isActive: false,
    isDisabled: false,
    onClickCallback: null,
    onClick: (isActive) => {
      set((state) => {
        const { raiseHandSettings } = state;
        // Ensure that onClickCallback is not null before calling it
        if (raiseHandSettings.onClickCallback) {
          raiseHandSettings.onClickCallback(isActive); // Call the callback if it exists
        } else {
          console.warn('raiseHandSettings.onClickCallback is not set');
        }
        return {
          raiseHandSettings: { ...raiseHandSettings, isActive }, //update the new state of isActive after run the callback
        };
      });
    },
  },
  setRaiseHandSettings: (raiseHandSettings) =>
    set((state) => ({
      raiseHandSettings: { ...state.raiseHandSettings, ...raiseHandSettings }, // Safely merge settings
    })),

  shareScreenSettings: {
    isActive: false,
    isDisabled: false,
    isLoading: false,
    trialLockEndTime: null,
    onClickCallback: null,
    onClick: (isActive) => {
      const { shareScreenSettings } = useRoomActionsStore.getState();

      // Prevent multiple clicks while loading (webinar-only feature)
      if (shareScreenSettings.isLoading && isActive) {
        return;
      }

      // Ensure that onClickCallback is not null before calling it
      if (shareScreenSettings.onClickCallback) {
        shareScreenSettings.onClickCallback(isActive); // Call the callback if it exists
      } else {
        console.warn('shareScreenSettings.onClickCallback is not set');
      }
    },
  },
  setShareScreenSettings: (shareScreenSettings) =>
    set((state) => ({
      shareScreenSettings: { ...state.shareScreenSettings, ...shareScreenSettings }, // Safely merge settings
    })),
  transcriptionSettings: {
    isActive: false,
    isDisabled: false,
    onClickCallback: null,
    onClick: (isActive) => {
      set((state) => {
        const { transcriptionSettings } = state;
        return {
          transcriptionSettings: { ...transcriptionSettings, isActive }, //update the new state of isActive after run the callback
        };
      });
    },
  },
  setTranscriptionSettings: (transcriptionSettings) =>
    set((state) => ({
      transcriptionSettings: { ...state.transcriptionSettings, ...transcriptionSettings }, // Safely merge settings
    })),
  // Share Room
  shareRoomSettings: {
    isActive: false,
    isDisabled: false,
    onClickCallback: null,
    onClick: (isActive) => {
      const meetingLink = window.location.href;
      const { meetingType } = useRoomStore.getState();
      const isWebinar = meetingType === 'webinar';
      const meetingTypeText = isWebinar ? 'Webinar' : 'Meeting';

      sendEventToWebView({
        eventType: EWebViewEvents.SHARE_TEXT,
        payload: {
          title: `Share ${meetingTypeText} Link`,
          extraTitle: `Join our ${meetingTypeText} on v`,
          text: `${meetingTypeText} link: ${meetingLink}`,
        },
      });

      set((state) => {
        const { shareRoomSettings } = state;
        // Ensure that onClickCallback is not null before calling it
        if (shareRoomSettings.onClickCallback) {
          shareRoomSettings.onClickCallback(isActive); // Call the callback if it exists
        } else {
          console.warn('shareRoomSettings.onClickCallback is not set');
        }
        return {
          shareRoomSettings: { ...shareRoomSettings, isActive }, //update the new state of isActive after run the callback
        };
      });
    },
  },
  setShareRoomSettings: (shareRoomSettings) =>
    set((state) => ({
      shareRoomSettings: { ...state.shareRoomSettings, ...shareRoomSettings }, // Safely merge settings
    })),

  screenRecordingSettings: {
    isActive: false,
    isDisabled: false,
    onClickCallback: null,

    onClick: (isActive) => {
      set((state) => {
        const { screenRecordingSettings } = state;
        // Ensure that onClickCallback is not null before calling it
        if (screenRecordingSettings.onClickCallback) {
          screenRecordingSettings.onClickCallback(isActive); // Call the callback if it exists
        } else {
          console.warn('screenRecordingSettings.onClickCallback is not set');
        }
        return state;
      });
    },
  },
  setScreenRecordingSettings: (screenRecordingSettings) =>
    set((state) => ({
      screenRecordingSettings: { ...state.screenRecordingSettings, ...screenRecordingSettings }, // Safely merge settings
    })),

  snapshotSettings: {
    isActive: false,
    isDisabled: false,
  },
  setSnapshotSettings: (snapshotSettings) =>
    set((state) => ({
      snapshotSettings: { ...state.snapshotSettings, ...snapshotSettings },
    })),

  fullscreenSettings: {
    isActive: false,
    isDisabled: false,
  },
  setFullscreenSettings: (fullscreenSettings) =>
    set((state) => ({
      fullscreenSettings: { ...state.fullscreenSettings, ...fullscreenSettings },
    })),
  hideSelfSettings: {
    isActive: false,
    isDisabled: false,
  },
  setHideSelfSettings: (hideSelfSettings) =>
    set((state) => ({
      hideSelfSettings: { ...state.hideSelfSettings, ...hideSelfSettings },
    })),

  whiteboardSettings: {
    isActive: false,
    isDisabled: false,
    onClickCallback: null,
    onClick: async () => {},
  },
  setWhiteboardSettings: (whiteboardSettings) =>
    set((state) => ({
      whiteboardSettings: { ...state.whiteboardSettings, ...whiteboardSettings },
    })),
}));
